import download from 'downloadjs';
import { toast } from 'react-toastify';
import watermark from 'watermarkjs';
import { iTHREE } from '@treedis/tour-loader';
import { TagType } from '@/_models/Tag';
import { Menu, MenuType } from '@/_models/Menu';
import { TourMode, TourType } from '@/_models/Tour';
import { ChapterType } from '@/_models/game/GameBook';
import { buildLink, getAllQueryVariables, getQueryVariable, getVersion, getVideoLink } from './all';
import socket from './socket';
import { sendToGA } from './analytic';
import { processNavigationPath } from '../tour/processNavigationPath';
import { moveToSweeps } from '../tour/moveToSweeps';
import { EntityType, LockLocationType } from '@/_models/Game';

function downloadDirect(url: string, title: string) {
  const element = document.createElement('a');
  element.setAttribute('href', url);
  element.setAttribute('download', `${title}.jpg`);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

export function takePano() {
  if (this.showcase) {
    this.setState({ loading: true });
    this.showcase.Renderer.takeEquirectangular().then((url) => {
      downloadDirect(url, this.state.tour.title);
      this.setState({ loading: false });
    });
  }
}

export function getLocation() {
  const m = getQueryVariable(this.state.iframeUrl, 'm');
  if (m) {
    return buildLink(this.pose.sweep, this.pose.rotation, btoa(encodeURIComponent(m)));
  }
  return null;
}

function downloadImage(title: string, url: string, clientId: number | null, width: number) {
  const options = {
    init(img) {
      // eslint-disable-next-line no-param-reassign
      img.crossOrigin = 'anonymous';
    },
  };
  watermark([url, `${process.env.REACT_APP_AWS}/clients/${clientId}/logo.png${getVersion(new Date())}`], options)
    .blob((target, _watermark) => {
      const context = target.getContext('2d');
      context.save();

      const widthRatio = _watermark.width / width;
      const height = _watermark.height / widthRatio;
      context.drawImage(_watermark, 10, 10, width, height);

      context.restore();
      return target;
    })
    .then((img) => download(img, `${title}.jpg`, 'image/jpg'));
}

export function takeScreenshot(resolution) {
  if (this.showcase) {
    const { tour } = this.state;
    const width = tour.client && tour.client.widthLogo ? 250 : 150;
    this.setState({ loading: true });
    this.showcase.Renderer.takeScreenShot(resolution, {
      mattertags: false,
      sweeps: false,
    }).then((url) => {
      if (tour.client && tour.client.logo) {
        downloadImage(tour.title, url, tour.clientId, width);
      } else {
        downloadDirect(url, tour.title);
      }
      this.setState({ loading: false });
    });
  }
}

export function preventTagAction(sid, actions) {
  if (this?.showcase) {
    // eslint-disable-next-line no-console
    return this.showcase.Mattertag.preventAction(sid, actions).catch(console.error);
  }
  return null;
}

export async function navigateToTag(sid) {
  if (this?.showcase) {
    const preventAction = preventTagAction.bind(this);
    await preventAction(sid, { navigation: false });
    // eslint-disable-next-line no-console
    await this.showcase.Mattertag.navigateToTag(sid, this.showcase.Mattertag.Transition.FLY).catch(console.error);
    await preventAction(sid, { navigation: true });
  }
}

export function loadModel(name: string, url: string, type: string, tag: string, panoramaId: number, changeMinimap = true, changePanorama = false, iframeSettings = {}) {
  if (type === MenuType.Tab) {
    window.open(url);
  } else if (type === MenuType.Popup) {
    this.toggleExternalPopup(url);
  } else if (type === MenuType.Iframe) {
    this.toggleExternalPopup(url, iframeSettings);
  } else if (type === MenuType.Product && tag !== null) {
    const products = this.state.products.filter((item) => {
      return (item?.tags || []).some((productTag: { sid: string }) => productTag.sid === tag);
    });
    if (products && products.length > 0) {
      this.setState({
        currentProducts: products.length > 1 ? products : [],
        currentProduct: products.length === 1 ? products[0] : null,
      });
    }
    navigateToTag(tag);
    window.location.hash = tag;
  } else if (type === MenuType.Panorama) {
    this.setState({
      panoramaOn: true,
      activePano: panoramaId,
    });
  } else if (type === MenuType.Treedis) {
    const { tour } = this.state;
    const segments = url.split('/');
    const lastSeg = segments[segments.length - 1].split('?');
    const id = lastSeg[0];
    const sameTour = tour.slug === id;

    if (sameTour && lastSeg.length === 1) return; // same tour, no query params, we shouldn't get it again
    if (url.includes('matterport.com')) {
      toast.error("Cannot use this link. To copy location in the tour, please use 'CTRL+Y'");
      return;
    }

    // if isVSReady is true, then VS is configured (VS components, lights etc.) - can't init VS without it
    // don't need to configure that if it's the same tour
    this.setState({ panoramaOn: false, activePano: null, isVSReady: sameTour }, async () => {
      this.getTour({ id, changeMenu: false, changeMinimap, changePanorama, url, hideIntroScreen: true }); // false = dont change the menu/panorama
    });
  } else if (type === MenuType.MatterportInstant) {
    this.setState({isChangeIframe: this.state.iframeUrl.matterId !== getQueryVariable(url, "m")}, () => {
      this.moveModel(`/mp-sdk/${process.env.REACT_APP_SDK_VERSION}/showcase.html?applicationKey=${this.state.tour?.user?.mpSDK}${getAllQueryVariables(url)}`, false, true);
    })
  } else if (type === MenuType.Tag && tag !== null) {
    const target = Array.from(this.state.allTags, ([, value]: any) => value)
      // searching by temporary sid from Matterport
      .find((item: any) => item.sid === tag || item.defaultSid === tag);

    if (!target) {
      toast.error('Cannot find the tag on the tour');
      return;
    }

    this.setState({ panoramaOn: false }, () => {
      this.moveToTagIO(target, false);
    });
  } else {
    this.setState({isChangeIframe: this.state.iframeUrl.matterId !== getQueryVariable(url, "m")}, () => {
      this.moveModel(`/mp-sdk/${process.env.REACT_APP_SDK_VERSION}/showcase.html?applicationKey=${this.state.tour?.user?.mpSDK}${getAllQueryVariables(url)}`, false, false);
    })
  }
  if (name) {
    sendToGA('event', {
      eventCategory: 'Place',
      eventAction: 'click',
      eventLabel: name,
    });
  }

  if (name && this.state.chatToken && socket && socket.connected && this.state.host) {
    socket.emit('menu', {
      menu: {
        name,
        url,
        type,
      },
    });
  }
}

export async function openedPanoHotspot(hotspot: any) {
  if (hotspot.type === TagType.Matterport && hotspot.url) {
    this.moveModel(`/mp-sdk/${process.env.REACT_APP_SDK_VERSION}/showcase.html?applicationKey=${this.state.tour?.user?.mpSDK}${getAllQueryVariables(hotspot.url)}`, false, true);
    return;
  }
  if (hotspot.type === TagType.MatterportInstant && hotspot.url) {
    this.moveModel(`/mp-sdk/${process.env.REACT_APP_SDK_VERSION}/showcase.html?applicationKey=${this.state.tour?.user?.mpSDK}${getAllQueryVariables(hotspot.url)}`, false, false);
    return;
  }

  if (hotspot.type === TagType.Treedis && hotspot.url) {
    const segments = hotspot.url.split('/');
    const id = hotspot.url.includes('?') ? segments[segments.length - 1].split('?')[0] : segments[4];
    const isHubUrl = hotspot.url.includes('host') || hotspot.url.includes('guest');
    const room = isHubUrl ? segments[segments.length - 1] : null;
    if (getQueryVariable(hotspot.url, 'm')) {
      toast.error("Can't use this link, to copy location in treedis tour, use CTRL+Y");
      return;
    }
    const { tour } = this.state;
    const changeMinimap = tour && !tour.customMinimap;
    await this.getTour({ id, changeMenu: false, changeMinimap, changePanorama: false, url: hotspot.url }); // false = dont change the menu/panorama

    if (!socket.connected && isHubUrl) {
      socket.open();
    }

    this.setState({ panoramaOn: false, activePano: null, isVSReady: false, room }, async () => {
      if (this.state.chatToken && socket && socket.connected && this.state.host) {
        socket.emit('pano-hotspot-click', {
          id,
          changeMenu: false,
          changeMinimap,
          changePanorama: false,
          url: hotspot.url,
        });
      }
    });
    return;
  }

  if (hotspot.type === TagType.Music && hotspot.url) {
    const { tour, panoramas } = this.state;
    if (tour.noAudioMode && this.state.chatToken) return;

    const panorama = panoramas.find((p: any) => p.id === hotspot.panoramaId);
    const src = `${process.env.REACT_APP_AWS}/tours/${tour.id}/panoramas/${panorama.url}/${hotspot.url}`;
    let audioContainer = this.getAudioContainer('panoramaAudio');

    if (audioContainer?.hotspotId !== hotspot.id) {
      audioContainer?.pause();
      audioContainer = this.createAudioStream('panoramaAudio', src);
      audioContainer.hotspotId = hotspot.id;
    }

    const play = async () => {
      try {
        await this.pauseAllAudio('panoramaAudio');
        const status = await this.playContainerAudio('panoramaAudio');

        this.setState({ mainPlaying: false, otherPlaying: status });
      } catch (error) {
        setTimeout(play, 500);
      }
    };

    play();
    return;
  }

  if (hotspot.type === TagType.Video && hotspot.url) {
    const { noAudioMode } = this.state?.tour;
    hotspot.url = getVideoLink(hotspot.url, {
      mute: noAudioMode && this.state.chatToken,
    });
  }
  this.setState({
    panoPopOn: true,
    openedPanoHotspot: hotspot,
  });
}

export async function openTagIframe(sid: string) {
  const { allTags, tour, tourType } = this.state;

  // already we have tag why we find one else
  const foundTag = Array.from(allTags, ([, value]: any) => value)
    // searching by temporary sid from Matterport
    .find((item: any) => item.sid === sid || item.defaultSid === sid);

  if (foundTag) {
    if (!foundTag.enabled || !foundTag.type) return null;

    const tag = { ...foundTag };

    if (tag.type === 'video') {
      await this.pauseTourMusic();
    }

    // Allow tag.type with NONE who participate in game actions like chapter, lock
    if (tour?.gameId && tourType === TourType.Game) {
      this.unlockChapter(tag, ChapterType.Tag);
      if (this.handleTagLock(tag)) return null;
    } else if (tour?.gameId) {
      this.setState({
        clickedElement: {
          id: tag.id,
          type: EntityType.Tag,
        },
      });
    }
    if (tag?.type === TagType.None) {
      return null;
    }
    if (tag?.type === TagType.Navigation) {
      if (this.state.tour?.showNavTools) {
        processNavigationPath({
          tagNavigationLink: tag.navigationLink,
          sweeps: this.state.sweeps,
          startSweep: this.pose.sweep,
          cb: ({ sweepsPath, rotation }) => {
            const { rotationAngle, rotationSpeed, transitionSpeed, stopAfterSweep } = this.state.tour;
            moveToSweeps({
              sweepsPath,
              targetRotation: rotation,
              rotationAngle,
              rotationSpeed,
              transitionSpeed,
              stopAfterSweep,
              poseRotation: this.pose.rotation,
              showcaseSweep: this.showcase.Sweep,
            }).then(() => {
              this.setState({
                showPathFinderPopup: true,
              });
            });
          },
        });
      }
      return null;
    }
    if (tag.embedMedia && [TagType.Image, TagType.Video, TagType.Iframe, TagType.Text].includes(tag.type)) {
      return null;
    }
    if (tag.type === TagType.Panorama) {
      this.setState({
        activePano: tag.panoramaId,
        panoramaOn: true,
      });
      return null;
    }
    if (tag.type === TagType.Treedis && tag.url) {
      const segmants = tag.url.split('/');
      const lastSeg = segmants[segmants.length - 1].split('?');
      const id = lastSeg[0];
      if (tour.slug === id && lastSeg.length === 1) return null; // same tour, no query params, we shouldn't get it again

      if (getQueryVariable(tag.url, 'm')) {
        toast.error("Cannot use this link. To copy location in the tour, please use 'CTRL+Y'");
        return null;
      }
      const changeMinimap = tour && !tour.customMinimap;
      // isVSReady - the VS should be initialized again
      this.setState({ panoramaOn: false, activePano: null, isVSReady: false }, async () => {
        await this.stopVS();
        this.modelNodes = [];
        this.vsObjects = [];
        this.getTour({ id, changeMenu: false, changeMinimap, changePanorama: false, url: tag.url, hideIntroScreen: true }); // false = dont change the menu/panorama
      });
      return null;
    }
    if (tag.type === TagType.MatterportInstant && tag.url) {
      this.moveModel(`/mp-sdk/${process.env.REACT_APP_SDK_VERSION}/showcase.html?applicationKey=${this.state.tour?.user?.mpSDK}${getAllQueryVariables(tag.url)}`, false, true);
      return null;
    }
    if (tag.type === TagType.Matterport && tag.url) {
      this.moveModel(`/mp-sdk/${process.env.REACT_APP_SDK_VERSION}/showcase.html?applicationKey=${this.state.tour?.user?.mpSDK}${getAllQueryVariables(tag.url)}`, false, false);
      return null;
    }
    if (tag.type === TagType.Menu) {
      this.setState(
        {
          menuOpen: false,
          activeMenu: null,
        },
        () => {
          this.setState({
            menuOpen: true,
            menuOpened: true,
            activeMenu: {
              id: tag.menuId,
              subMenu: tag.subMenuId,
              subSubMenu: tag.subSubMenuId,
            },
          });
        },
      );
      return null;
    }
    if (tag.type === TagType.Video && tag.url) {
      const { noAudioMode } = this.state?.tour;
      tag.url = getVideoLink(tag.url, {
        mute: noAudioMode && this.state.chatToken,
        autoplay: tag.autoPlay,
      });
      this.pauseAllAudio();
    }
    if (tag.type === TagType.Tab && tag.url) {
      window.open(tag.url);
      return null;
    }
    if (tag.type === TagType.Music && tag.url) {
      const { noAudioMode } = this.state?.tour;
      if (noAudioMode && this.state.chatToken) return null;

      const src = `${process.env.REACT_APP_AWS}/tours/${this.state.tour.id}/tags/${tag.url}`;
      let audioContainer = this.getAudioContainer('tagAudio');

      if (audioContainer?.tagId !== tag.id) {
        audioContainer = this.createAudioStream('tagAudio', src, true);
        audioContainer.tagId = tag.id;
      }

      const play = async () => {
        try {
          await this.pauseAllAudio('tagAudio');
          const status = await this.playContainerAudio('tagAudio');

          this.setState({ mainPlaying: false, otherPlaying: status });
        } catch (error) {
          if (error instanceof DOMException) {
            // eslint-disable-next-line no-console
            console.error(error.message);
          }
          setTimeout(play, 500);
        }
      };

      play();
      return null;
    }
    if (tag.type === TagType.VideoChat) {
      this.setState({
        videoChatTagPopOn: true,
        openedTag: {
          ...tag,
          sid: tag.sid,
        },
      });
      this.pauseAllAudio();
      return null;
    }
    if (tag.type === TagType.ConferenceChat) {
      this.setState({
        chatOpen: true,
        chatRoom: {
          tag: {
            ...tag,
            sid: tag.sid,
          },
          client: tag?.client,
        },
      });
      return null;
    }

    this.setState({
      tagPopOn: true,
      openedTag: tag,
      closedElementPopup: false,
    });

    return tag;
  }
  return null;
}

export async function moveToMPLabels(menu: Menu) {
  const { type, labelId, floorId } = menu;
  const { mpLabels, sweeps, mpFloors } = this.state;
  const THREE = await iTHREE();

  try {
    switch (type) {
      case MenuType.MatterportFloor:
        // ToDo: menu.floorId from old API so we can not be sure that it work constantly
        const floorIndex = mpFloors.findIndex((l) => String(l.id) === String(floorId));
        await this.changeViewMode(TourMode.FLOORPLAN);
        await this.changeFloor(floorIndex);
        break;
      case MenuType.MatterportLabel:
        const label = mpLabels.find((l) => l.sid === labelId);
        if (!label) console.error('Label not found');

        const sweepsOnFloor = sweeps.filter((x) => x.floorInfo.id === label.floorInfo.id && x.enabled);

        if (sweepsOnFloor.length === 0) console.error('Sweeps not found');

        const startPoint = new THREE.Vector3(label.position.x, label.position.y, label.position.z);
        const distances = sweepsOnFloor.map((sweep) => {
          const endPoint = new THREE.Vector3(sweep.position.x, sweep.position.y, sweep.position.z);
          const distance = Math.abs(startPoint.distanceTo(endPoint));
          return {
            distance,
            ...sweep,
          };
        });

        const sweepsWithMinDistance = distances.reduce((a, b) => (a?.distance <= b?.distance ? a : b));
        // eslint-disable-next-line dot-notation
        await this.showcase.Sweep.moveTo(sweepsWithMinDistance['id'], {
          // eslint-disable-next-line dot-notation
          rotation: sweepsWithMinDistance['rotation'],
          transition: this.showcase.Mode.TransitionType.FLY,
        });
        break;
      default:
        console.error('moveToMPLabels: Menu type unknown');
        break;
    }
  } catch (e) {
    console.error(e);
  }
}

export async function openVsObjectIframe(vsObject: any) {
  const { tour } = this.state;
  if (tour?.gameId) {
    this.unlockChapter(vsObject, ChapterType.VSItem);
    if (this.handleVsObjectLock(vsObject, LockLocationType.vsItem)) return null;
  }
}

export async function openVsImageIframe(vsImage: any) {
  const { tour } = this.state;
  if (tour?.gameId) {
    this.unlockChapter(vsImage, ChapterType.VSImage);
    if (this.handleVsObjectLock(vsImage, LockLocationType.vsImage)) return null;
  }
}

export async function openVsVideoIframe(vsVideo: any) {
  const { tour } = this.state;
  if (tour?.gameId) {
    this.unlockChapter(vsVideo, ChapterType.VSVideo);
    if (this.handleVsObjectLock(vsVideo, LockLocationType.vsVideo)) return null;
  }
}
